import { useState } from "react";
import ErrorModal from "../modals/ErrorModal";
import { formatDate } from "../../utils/dateUtils";
import { FetchedReportData, ReportStatusType } from "../../types";
import { formattedVehicleInfo, socialsJointText } from "./resources";
import ReportStatusSelector from "../Admin/AdminDashboard/ReportStatusSelector";
import { useReportStatus } from "../hooks/useReportStatus";
import AudioPlayer from "../common/AudioPlayer";
import ImageGallery from "../common/ImageGallery";
import { capitalizeFirstLetters, getMediaUrl } from "../../utils/stringUtils";
import TruncatedText from "../common/TruncatedText";
import { FaCheck, FaSpinner, FaX } from "react-icons/fa6";

// API base URL for fetching media

const ReportSection = ({
  label,
  value,
  isBold = false,
}: {
  label: string;
  value: string;
  isBold?: boolean;
}) => (
  <div className="w-full flex flex-col items-start gap-6">
    <p
      className={`bg-gray-7 rounded-3xl text-white py-3 px-6 text-sm leading-4 font-${
        isBold ? "extrabold" : "normal"
      }`}
    >
      {label}
    </p>
    <p className="flex self-end bg-transparent rounded-3xl text-gray-7 border border-gray-7 py-3 px-6 text-sm leading-4 font-normal text-right">
      {value ?? "Not Available"}
    </p>
  </div>
);

// New component for media sections
const MediaSection = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="w-full flex flex-col items-start gap-4">
    <p className="bg-gray-7 rounded-3xl text-white py-3 px-6 text-sm leading-4 font-normal">
      {label}
    </p>
    <div className="w-full">{children}</div>
  </div>
);

const ReportStatus = ({
  reportStatus,
  reportId,
  handleSetStatus,
  loading,
}: {
  reportStatus?: ReportStatusType;
  reportId?: string;
  handleSetStatus: (newStatus: ReportStatusType) => void;
  loading: boolean;
}) => {
  const [status, setStatus] = useState<ReportStatusType | undefined>(
    reportStatus
  );

  if (!reportId || !reportStatus) return null;

  if (!reportId) return null;

  const handleChangeStatus = () => {
    if (!status) return;

    handleSetStatus(status);
  };

  return (
    <div className="flex items-center justify-start gap-2">
      <ReportStatusSelector
        reportStatus={status}
        setReportStatus={setStatus}
        simpleView={true}
      />

      {loading ? (
        <FaSpinner className="animate-spin" size={12} />
      ) : (
        status !== reportStatus && (
          <div className="flex gap-2">
            <FaX
              size={12}
              className="text-danger cursor-pointer"
              onClick={() => setStatus(reportStatus)}
            />
            <FaCheck
              size={12}
              className="text-success cursor-pointer"
              onClick={handleChangeStatus}
            />
          </div>
        )
      )}
    </div>
  );
};

const ReportViewCard = ({
  report,
  className,
  isUser = false,
  onStatusUpdate,
}: {
  report?: FetchedReportData;
  className?: string;
  isUser?: boolean;
  onStatusUpdate?: (reportId: string, newStatus: ReportStatusType) => void;
}) => {
  const {
    _id,
    caseName,
    caseType,
    incidentDescription,
    hasAbuserInfo,
    reporterDetails,
    abuserDetails,
    NeedAnAttorney,
    NeedCounseling,
    NeedPrivateInvestigator,
  } = report || {};

  const { loading, error, status, handleSetStatus, clearError } =
    useReportStatus(_id, report?.status, onStatusUpdate);

  const formattedDate = incidentDescription?.date
    ? formatDate(incidentDescription?.date, "mmddyyyy")
    : undefined;

  // Process media URLs

  // Handle voice narration audio
  const voiceNarrationUrl = incidentDescription?.incidentVoiceNarration
    ? getMediaUrl(incidentDescription.incidentVoiceNarration[0])
    : "";

  // Handle incident evidence images
  const evidenceImageUrls = incidentDescription?.incidentPhotoEvidence
    ? incidentDescription.incidentPhotoEvidence.map(getMediaUrl)
    : [];

  // Handle abuser photo evidence
  const abuserPhotoUrls =
    hasAbuserInfo && abuserDetails?.abuserPhotoDescription
      ? abuserDetails.abuserPhotoDescription.map(getMediaUrl)
      : [];

  return (
    <div
      className={`flex flex-col border-[3px] rounded-[16px] border-gray-7 pb-[40px] ${className}`}
    >
      <div className="flex justify-between w-full text-gray-7 py-4 border-b-none md:border-b-[1.5px] md:border-gray-3 px-6">
        <div className="flex flex-col justify-start items-start gap-[10px]">
          <h2 className="font-bold text-[20px] leading-[26px]">
            CASE N<sup>o</sup>:{" "}
            <TruncatedText
              maxLength={30}
              text={_id}
              textClasses="text-gray-7 text-base"
            />
          </h2>
          {!isUser && (
            <ReportStatus
              reportStatus={status}
              reportId={report?._id}
              handleSetStatus={handleSetStatus}
              loading={loading}
            />
          )}
        </div>
        <h3 className="font-semibold text-xl leading-6">
          Date: {formattedDate}
        </h3>
      </div>

      {/* Case type choice */}
      <div className="flex flex-col flex-grow overflow-y-auto mt-[26px] pt-4 pl-6 pr-3 mr-3 gap-6">
        <ReportSection
          label="I want to report"
          value={caseName || "Not Available"}
          isBold
        />
        <ReportSection
          label="Select sub-case"
          value={caseType || "Not Available"}
        />
        <ReportSection
          label="Date of the incident"
          value={formattedDate || "Not Available"}
        />
        <ReportSection
          label="Where did this incident occur"
          value={incidentDescription?.location || "Not Available"}
        />

        {["social media", "online"].includes(
          incidentDescription?.location || ""
        ) && (
          <ReportSection
            label="Online Locations/Links"
            value={
              incidentDescription?.digitalcaseLinks?.join(", ") ||
              "Not Available"
            }
          />
        )}

        <ReportSection
          label="Location of the incident"
          value={`${capitalizeFirstLetters(
            incidentDescription?.detailedLocation.city
          )}, 
          ${capitalizeFirstLetters(
            incidentDescription?.detailedLocation.county
          )}, 
          ${capitalizeFirstLetters(
            incidentDescription?.detailedLocation.state
          )}`}
        />
        <ReportSection
          label="Describe the incident"
          value={incidentDescription?.description || "Not Available"}
        />

        {/* Voice Narration */}
        {voiceNarrationUrl ? (
          <MediaSection label="Voice narration">
            <AudioPlayer
              src={voiceNarrationUrl}
              title="Incident Voice Narration"
            />
          </MediaSection>
        ) : (
          <ReportSection
            label="Voice narration"
            value="No voice narration provided"
          />
        )}

        {/* Photo Evidence */}
        {evidenceImageUrls.length > 0 ? (
          <MediaSection label="Abuse photo evidence">
            <ImageGallery
              images={evidenceImageUrls}
              title="Incident Evidence Photos"
            />
          </MediaSection>
        ) : (
          <ReportSection
            label="Abuse photo evidence"
            value="No photo evidence provided"
          />
        )}

        {/* Has abuser info */}
        <ReportSection
          label="Do you have any information on who committed the incident?"
          value={hasAbuserInfo ? "Yes" : "No"}
        />

        {/* Reporter details */}
        <ReportSection
          label="Are you a?"
          value={reporterDetails?.role || "Not Available"}
        />
        <ReportSection
          label="About you..."
          value={`Gender: ${reporterDetails?.gender || "Not Available"}, 
          Age: ${reporterDetails?.age || "Not Available"}, 
          Phone: ${reporterDetails?.phone || "Not Available"}, 
          E-mail: ${reporterDetails?.email || "Not Available"}${
            Object.entries(reporterDetails?.socials || {}).length > 0
              ? `, ${Object.entries(reporterDetails?.socials || {})
                  .filter(([, handle]) => handle) // Ensure handle exists
                  .map(([social, handle]) => `${social}: ${handle}`)
                  .join(", ")}`
              : ""
          }`}
        />

        {hasAbuserInfo && (
          <>
            {/* Abuser details */}
            <ReportSection
              label="About the one who committed the incident"
              value={`${abuserDetails?.name || "Not Available"}, ${
                abuserDetails?.nickname || "Not Available"
              }, ${abuserDetails?.gender || "Not Available"}, ${
                abuserDetails?.refAge || "Not Available"
              }, ${abuserDetails?.relationToVictim || "Not Available"}, 
              ${abuserDetails?.occupation || "Not Available"},
              ${socialsJointText(abuserDetails?.socials)}
              ${
                abuserDetails?.hasSpecialAuthority
                  ? `, Special authority: ${
                      abuserDetails.specialAuthorityPosition || "Not Available"
                    }`
                  : ""
              }`}
            />

            {/* Abuser Photos */}
            {abuserPhotoUrls.length > 0 ? (
              <MediaSection label="Abuser Photos">
                <ImageGallery
                  images={abuserPhotoUrls}
                  title="Photos of the Abuser"
                />
              </MediaSection>
            ) : (
              <ReportSection label="Abuser Photos" value="No photos provided" />
            )}

            {/* Abuser's physical description */}
            <ReportSection
              label="Abuser's Physical Description"
              value={abuserDetails?.phyDescription || "Not Available"}
            />

            {/* Abuser's vehicle description */}
            {abuserDetails?.hasAbuserVehicleInfo && (
              <ReportSection
                label="Abuser's vehicle description"
                value={formattedVehicleInfo(abuserDetails.vehicleInfo)}
              />
            )}
          </>
        )}

        {/* Support services section */}
        <ReportSection
          label="Need an attorney?"
          value={NeedAnAttorney ? "Yes" : "No"}
        />
        <ReportSection
          label="Need counseling?"
          value={NeedCounseling ? "Yes" : "No"}
        />
        <ReportSection
          label="Need a private investigator?"
          value={NeedPrivateInvestigator ? "Yes" : "No"}
        />

        {error && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md z-50">
            <ErrorModal
              onAccept={clearError}
              onClose={clearError}
              errorDescription={error}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportViewCard;
